import type { RouteRecordRaw } from 'vue-router';
import type { ResolvedMonospaceSpace } from '#layers/data-model/public';
import type { WithPageMeta, WithSpaceMeta } from '#shared/types/space';
import { cleanDoubleSlashes } from 'ufo';
import { useRouter } from 'vue-router';
import { useStore } from '#imports';
import { getSpacePageRouteParams } from '#layers/spaces/utils/route-params';
import { getSpaceFirstPage } from '#layers/spaces/utils/space';
import { ErrorPage } from '#layers/ui/public';
import { SpacePageRouteMarker, SpaceRouteMarker } from '#shared/constants/space';

export function isSpaceRoute<T extends object>(route: T): route is WithSpaceMeta<T> {
	return 'meta' in route && !!route.meta && typeof route.meta === 'object' && SpaceRouteMarker in route.meta;
}

export function isSpacePageRoute<T extends object>(route: T): route is WithPageMeta<T> {
	return 'meta' in route && !!route.meta && typeof route.meta === 'object' && SpacePageRouteMarker in route.meta;
}

const DYNAMIC_ROUTE_REGEX = /(?:^|[^\\]):/;

export function isDynamicRoute(route: string): boolean {
	return DYNAMIC_ROUTE_REGEX.test(route);
}

interface CreateSpaceRoutesOptions {
	spaces: ResolvedMonospaceSpace[];
}

export function createSpaceRoutes({
	spaces,
}: CreateSpaceRoutesOptions): RouteRecordRaw[] {
	const routes: RouteRecordRaw[] = [];

	// Build a map of routes
	for (const space of spaces) {
		const firstPage = getSpaceFirstPage(space);
		const spaceRoute: RouteRecordRaw = {
			name: space.id,
			path: cleanDoubleSlashes([`:spaceSlug(.*)?-${space.id}`, space.route].map((p) => p.trim()).filter(Boolean).join('/')),
			component: () => import('#layers/spaces/components/Space/SpaceRender.vue'),
			props: {
				id: space.id,
			},
			redirect: firstPage
				? () => ({
						name: firstPage.id,
						params: getSpacePageRouteParams(firstPage),
					})
				: undefined,
			meta: {
				space: {
					title: space.title,
					icon: space.icon || 'lucide:file',
					sort: space.sort,
				},
				layout: 'workspace',
				[SpaceRouteMarker]: true,
			},
			children: [],
		};
		routes.push(spaceRoute);

		// TODO page folders

		const pages = space.pages;

		// Construct page tree

		/**
		 * Route record name -> Children route records
		 */
		const pageTree = new Map<string, RouteRecordRaw[]>();

		for (const page of pages) {
			let childrenRoutes = pageTree.get(page.id);

			if (!childrenRoutes) {
				childrenRoutes = [];
				pageTree.set(page.id, childrenRoutes);
			}

			const pageRoute: RouteRecordRaw = {
				name: page.id,
				path: cleanDoubleSlashes([`:pageSlug(.*)?-${page.id}`, page.route].map((p) => p.trim()).filter(Boolean).join('/')),
				component: () => import('#layers/spaces/components/Space/PageRender.vue'),
				props: {
					id: page.id,
					spaceId: space.id,
				},
				meta: {
					page: {
						id: page.id,
						title: page.title,
						icon: page.icon || 'lucide:file',
						sort: page.sort,
					},
					[SpacePageRouteMarker]: true,
				},
				children: childrenRoutes,
			};

			// Insert into tree
			if (page.parentPageId) {
				let parentChildren = pageTree.get(page.parentPageId);

				// In case the parent page route record has not been created yet
				if (!parentChildren) {
					parentChildren = [];
					pageTree.set(page.parentPageId, parentChildren);
				}

				parentChildren.push(pageRoute);
			}
			else {
				// 'Root' page of the space
				spaceRoute.children.push(pageRoute);
			}
		}

		spaceRoute.children.push({
			name: `${space.id}--catch-all`,
			path: ':path(.*)*',
			component: ErrorPage,
		});
	}

	return routes;
}

export async function appendInitialSpaceRoutes(studioRoutes: readonly RouteRecordRaw[]) {
	const store = useStore();

	const spaces = await store.MonospaceSpace.findMany({
		include: {
			pages: true,
		},
	});

	const routes = createSpaceRoutes({
		spaces,
	});

	const workspaceRoute = studioRoutes.find(
		(route) => route.name === 'workspace-base',
	);

	workspaceRoute?.children?.push(...routes);

	return studioRoutes;
}

interface UpdateSpaceRoutesOptions extends CreateSpaceRoutesOptions {
	/**
	 * By default, updating the space routes will also refresh the current route
	 * to ensure that the current route contains the new route records.
	 *
	 * Set this to `true` to skip this behavior if you call `updateRoutes` during a navigation.
	 */
	skipCurrentRouteUpdate?: boolean;
}

export function useUpdateSpaceRoutes() {
	const router = useRouter();

	function updateRoutes(options: UpdateSpaceRoutesOptions) {
		const routes = createSpaceRoutes(options);

		for (const route of routes) {
			router.addRoute('workspace-base', route);
		}

		if (!options.skipCurrentRouteUpdate) {
			// Force reloading the current route to apply the new routes records
			// to avoid stale record objects in the `currentRoute`
			router.replace(router.currentRoute.value.fullPath);
		}

		return {
			revert: () => {
				for (const route of routes) {
					router.removeRoute(route.name!);
				}
			},
		};
	}

	return {
		updateRoutes,
	};
}
